import { Button } from '@progress/kendo-react-buttons';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { Error } from '@progress/kendo-react-labels';
import useFormField from 'app/hooks/form-fields/useFormField';
import { lettersNumbersAndSpecialsRegex } from 'app/common/utils';
import useInputPatternValidation from 'app/hooks/form-fields/useInputPatternValidation';
import useClickOutside from 'app/hooks/useClickOutside';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import { useNotificationsContext } from 'context';
import { Success } from 'app/components/Notifications';
import { userInfoApi } from 'services/api/user-info/queries';

interface Props {
  initialValue: string;
  onChange: (value: string) => void;
  close: () => void;
}

export default function EditCompanyNameDialog({
  initialValue,
  onChange,
  close,
}: Props) {
  const { t } = useTranslation();

  const [updateUser, { isLoading }] = userInfoApi.useUpdateUserInfoMutation();
  const { addErrorModal } = React.useContext(GlobalErrorContext);
  const { addNotification } = useNotificationsContext();

  // Note: there's a point in creating a wrapper around the Dialog and adding an onClickOutside prop
  useClickOutside(close);

  const { value, setValue, error, setError } = useFormField(initialValue);

  const { isPatternMatch, onKeyDown } = useInputPatternValidation(
    lettersNumbersAndSpecialsRegex,
  );

  async function handleUpdate() {
    try {
      await updateUser({
        companyName: value === '' ? null : value?.trim(),
      });
      addNotification(<Success text={t('success_company_name_update')} />);
      onChange(value?.trim() || '');
      close();
    } catch (error) {
      addErrorModal();
    }
  }

  function validateName() {
    if (value && value.trim().length > 200) {
      setError(t('name_length_200_exceeded'));
      return false;
    }

    return true;
  }

  const handleChange = (e: InputChangeEvent) => {
    const inputValue = e.target.value as string;

    if (isPatternMatch(inputValue)) {
      setValue(inputValue);
      setError('');
    }
  };

  return (
    <Dialog className="modal-form">
      <div className="modal-container relative">
        <span
          className="back-button absolute"
          onClick={() => {
            close();
          }}
        >
          <svg
            id="ic_nav_back"
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 48 48"
          >
            <rect id="Box" width="48" height="48" fill="none" />
            <path
              id="ic_nav_up"
              d="M16.465,8.482H1.483"
              transform="translate(15.518 15.518)"
              fill="none"
              stroke="#1a1a1a"
              strokeLinecap="round"
              strokeWidth="2"
            />
            <path
              id="Path_470"
              data-value="Path 470"
              d="M23.676,16.018,16.2,23.748l7.477,7.73"
              transform="translate(0.324 0.252)"
              fill="none"
              stroke="#1a1a1a"
              strokeLinecap="round"
              strokeWidth="2"
            />
          </svg>
        </span>
        <>
          <h2>{t('edit_company_name')}</h2>
          <div className={`form-group ${error && 'error'}`}>
            <div className="form-content">
              <div className="form-label">
                <span>{t('company_name')}</span>
              </div>
              <div className="form-control">
                <Input
                  id="companyName"
                  name="companyName"
                  autoComplete="off"
                  maxLength={100}
                  placeholder={t('enter_company_name')}
                  value={value}
                  onKeyDown={onKeyDown}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </div>
            </div>
            <div className="form-error">
              {error && <Error id={'error'}>{error}</Error>}
            </div>
          </div>
          <Button
            themeColor="primary"
            onClick={() => validateName() && handleUpdate()}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="loading_icon loading_icon-btn">
                <div className="css-icon"></div>
              </div>
            ) : (
              t('save')
            )}
          </Button>
        </>
      </div>
    </Dialog>
  );
}
